import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import "./App.css";

// --- Utilities ---
import { initSmoothScroll } from "./utils/scroll";
import { attachRipple } from "./utils/ripple";

// --- Data ---
import { projectData } from "./data/portfolioData";

// --- Components ---
import Header from "./components/Header";
import Hero from "./components/Hero";
import ProjectCarousel from "./components/ProjectCarousel";
import Skills from "./components/Skills";
import ContactForm from "./components/ContactForm";
import Contact from "./components/Contact";
import Modal from "./components/Modal";
import ProjectsHeader from "./components/ProjectsHeader";
import AiAssistantWidget from "./components/AiAssistantWidget";
import Footer from "./components/Footer";
import TechMarquee from "./components/TechMarquee";

// --- Pages ---
import CaseStudyPage from "./pages/CaseStudyPage";
import NotFoundPage from "./pages/NotFoundPage";
import ErrorBoundary from "./components/ErrorBoundary";

// --- Lazy-load heavy sections ---
const Experience = lazy(() => import("./components/Experience"));
const About = lazy(() => import("./components/About"));

// ─── Main portfolio home page ──────────────────────────────────────────────
function PortfolioHome() {
  const navigate = useNavigate();
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [isResumeOpen, setIsResumeOpen] = useState(false);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
  const lenisRef = useRef(null);
  const progressRef = useRef(null);

  // Lenis smooth scroll (home page only)
  useEffect(() => {
    const lenis = initSmoothScroll();
    lenisRef.current = lenis;
    return () => {
      lenisRef.current?.destroy?.();
      lenisRef.current = null;
    };
  }, []);

  // Scroll progress bar
  useEffect(() => {
    const bar = progressRef.current;
    if (!bar) return;
    const update = () => {
      const scrolled = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      bar.style.width = ((scrolled / max) * 100).toFixed(2) + "%";
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, []);

  // Ripple
  useEffect(() => {
    // Event-delegated ripple — attaches once, works for all buttons including
    // those rendered after mount (lazy sections, modals)
    attachRipple(".cta-button, .project-card, .contact-button");
  }, []);

  // Lock scroll when any modal is open
  useEffect(() => {
    const open = isContactFormOpen || isResumeOpen || isAiAssistantOpen;
    if (open) {
      document.body.style.overflow = "hidden";
      lenisRef.current?.stop?.();
    } else {
      document.body.style.overflow = "";
      lenisRef.current?.start?.();
    }
    return () => {
      document.body.style.overflow = "";
      lenisRef.current?.start?.();
    };
  }, [isContactFormOpen, isResumeOpen, isAiAssistantOpen]);

  // Prevent Lenis hijacking scroll inside modals
  useEffect(() => {
    const stop = (e) => {
      if (e.target?.closest?.("[role='dialog'], [data-modal='true']")) {
        e.stopPropagation();
      }
    };
    window.addEventListener("wheel",     stop, { capture: true, passive: false });
    window.addEventListener("touchmove", stop, { capture: true, passive: false });
    return () => {
      window.removeEventListener("wheel",     stop, { capture: true });
      window.removeEventListener("touchmove", stop, { capture: true });
    };
  }, []);

  const allProjects       = Object.values(projectData);
  const featuredProjects  = allProjects.filter((p) => p.category === "featured");
  const aiProjects        = allProjects.filter((p) => p.category === "ai");
  const leadershipProjects = allProjects.filter((p) => p.category === "leadership");

  // Cards now navigate to the case-study route
  const handleOpenProject = (projectId) => navigate(`/projects/${projectId}`);

  const handleOpenResume = () => {
    setIsResumeOpen(true);
    const link = document.createElement("a");
    link.href = "/Morpheus_Resume.pdf";
    link.download = "Morpheus_Resume.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* Scroll progress indicator */}
      <div
        className="scroll-progress"
        ref={progressRef}
        role="progressbar"
        aria-label="Page scroll progress"
        aria-valuemin={0}
        aria-valuemax={100}
      />

      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header onOpenResume={handleOpenResume} />

      <main id="main-content">
        <Hero onOpenModal={handleOpenProject} />

        {/* Animated tech skills marquee — between hero and projects */}
        <TechMarquee />

        <ProjectsHeader />

        <ProjectCarousel
          id="projects"
          title="Featured Projects"
          projects={featuredProjects}
          onOpenModal={handleOpenProject}
        />
        <ProjectCarousel
          title="AI & Data Science"
          projects={aiProjects}
          onOpenModal={handleOpenProject}
        />
        <ProjectCarousel
          title="Leadership & Initiatives"
          projects={leadershipProjects}
          onOpenModal={handleOpenProject}
        />

        <Skills />

        <hr className="section-divider" aria-hidden="true" />

        <Suspense fallback={<div className="section-loading">Loading Experience…</div>}>
          <Experience />
        </Suspense>

        <hr className="section-divider" aria-hidden="true" />

        <Suspense fallback={<div className="section-loading">Loading About…</div>}>
          <About />
        </Suspense>

        <hr className="section-divider" aria-hidden="true" />

        <Contact onOpenContactForm={() => setIsContactFormOpen(true)} />
      </main>

      <Footer />

      {/* Contact modal */}
      <Modal open={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} title="Get in Touch">
        <ContactForm showHeader={false} onSuccess={() => setIsContactFormOpen(false)} />
      </Modal>

      {/* Resume modal */}
      <Modal open={isResumeOpen} onClose={() => setIsResumeOpen(false)} title="My Resume">
        <div style={{ height: "75vh", width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
          <iframe
            src="/Morpheus_Resume.pdf"
            title="Morpheus Resume"
            style={{ width: "100%", flex: 1, border: "none", borderRadius: "8px" }}
          />
          <a
            href="/Morpheus_Resume.pdf"
            download="Morpheus_Resume.pdf"
            className="contact-button"
            style={{ alignSelf: "center", textAlign: "center", width: "fit-content" }}
          >
            Download Again
          </a>
        </div>
      </Modal>

      <AiAssistantWidget isOpen={isAiAssistantOpen} onToggle={setIsAiAssistantOpen} />
    </>
  );
}

// ─── App root with router ──────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/"                    element={<PortfolioHome />} />
        <Route path="/projects/:projectId" element={<ErrorBoundary><CaseStudyPage /></ErrorBoundary>} />
        <Route path="*"                    element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
